import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { X, Cookie } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  hasConsentDecision,
  acceptAllCookies,
  rejectNonEssentialCookies,
  loadAnalyticsIfConsented,
} from '@/lib/consent'

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    // Check if user has already made a decision
    if (!hasConsentDecision()) {
      setShowBanner(true)
    } else {
      // User has consented before, load analytics if they accepted
      loadAnalyticsIfConsented()
    }
  }, [])

  const handleAccept = () => {
    acceptAllCookies()
    setShowBanner(false)
    loadAnalyticsIfConsented()
  }

  const handleReject = () => {
    rejectNonEssentialCookies()
    setShowBanner(false)
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="mx-auto max-w-4xl">
        <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/50 p-6 md:p-8">
          {/* Close button */}
          <button
            onClick={handleReject}
            className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Icon and text */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Cookie Preferences
                </h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                We use cookies to enhance your experience. Essential cookies are required for the
                platform to function. Analytics cookies help us understand how you use Orbys360.{' '}
                <Link
                  to="/cookies"
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                >
                  Learn more
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleReject}
                className="h-11 px-6 border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Essential Only
              </Button>
              <Button
                onClick={handleAccept}
                className="h-11 px-6 bg-slate-900 hover:bg-slate-800 text-white"
              >
                Accept All
              </Button>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              By clicking "Accept All", you consent to analytics cookies.
              You can change your preferences anytime.
              See our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                Privacy Policy
              </Link>{' '}
              for details.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
